import React, { useEffect, useMemo, useState } from 'react';
import { Megaphone, Send, Trash2, Users, UserCheck, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { PageHeader, Card, Avatar } from './shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const timeAgo = (iso) => {
  if (!iso) return '';
  const m = Math.floor(Math.max(0, Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'Ahora'; if (m < 60) return `Hace ${m} min`;
  const h = Math.floor(m / 60); if (h < 24) return `Hace ${h} h`;
  return `Hace ${Math.floor(h / 24)} d`;
};

const QUICK = [
  '🍕 2x1 en pizzas hoy hasta las 9 pm. Pide desde la app.',
  '🚚 Envío gratis en tu próximo pedido mayor a L 200.',
  '☕ Nuevas cafeterías disponibles en Delibery 7. ¡Échales un ojo!',
];

const AdminCustomerNotifications = () => {
  const { customers, customerCampaigns, createCustomerCampaign, deleteCustomerCampaign, refreshCustomerCampaigns } = useApp();
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [scope, setScope] = useState('all');
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  useEffect(() => { refreshCustomerCampaigns(); }, [refreshCustomerCampaigns]);

  const list = useMemo(() => customers || [], [customers]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const targetCount = scope === 'all' ? list.length : selected.length;

  const send = async () => {
    if (!text.trim()) return toast.error('Escribe el contenido de la promoción');
    if (scope === 'selected' && selected.length === 0) return toast.error('Selecciona al menos un cliente');
    setBusy(true);
    try {
      const camp = await createCustomerCampaign({ title: title.trim() || null, text: text.trim(), customerIds: scope === 'selected' ? selected : null });
      toast.success(`Promoción enviada a ${camp.count} cliente${camp.count === 1 ? '' : 's'}`);
      setTitle(''); setText(''); setSelected([]); setScope('all');
    } catch { /* toast shown by context */ } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Promociones a clientes" subtitle="Envía ofertas y avisos a la bandeja de notificaciones de la app del cliente" />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <Card className="lg:col-span-3">
          <div className="flex items-center gap-2 mb-4"><div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center"><Megaphone size={20} /></div><div><h3 className="font-extrabold text-[#1E2130]">Nueva promoción</h3><p className="text-xs text-gray-500">Llega a la campanita del cliente al instante</p></div></div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Título (opcional)</Label>
              <Input data-testid="cn-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. 2x1 en pizzas" maxLength={80} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Mensaje</Label>
              <Textarea data-testid="cn-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={500} placeholder="Ej. Hoy 2x1 en todas las pizzas hasta las 9 pm." />
              <div className="flex flex-wrap gap-1.5">
                {QUICK.map((q, i) => <button key={i} type="button" data-testid={`cn-quick-${i}`} onClick={() => setText(q)} className="text-[11px] px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">{q.length > 42 ? `${q.slice(0, 40)}…` : q}</button>)}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold text-gray-600">Destinatarios</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="cn-scope-all" onClick={() => setScope('all')} className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors ${scope === 'all' ? 'border-[#EE3B36] bg-[#FFF7F7]' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <Users size={18} className={scope === 'all' ? 'text-[#EE3B36]' : 'text-gray-400'} />
                  <div><p className="text-sm font-semibold">Todos</p><p className="text-[11px] text-gray-500">{list.length} clientes</p></div>
                </button>
                <button type="button" data-testid="cn-scope-selected" onClick={() => setScope('selected')} className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors ${scope === 'selected' ? 'border-[#EE3B36] bg-[#FFF7F7]' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <UserCheck size={18} className={scope === 'selected' ? 'text-[#EE3B36]' : 'text-gray-400'} />
                  <div><p className="text-sm font-semibold">Seleccionar</p><p className="text-[11px] text-gray-500">{selected.length} elegidos</p></div>
                </button>
              </div>
            </div>

            {scope === 'selected' && (
              <div className="border border-gray-100 rounded-xl divide-y divide-gray-100 max-h-64 overflow-y-auto" data-testid="cn-customer-list">
                {list.length === 0 && <p className="text-center text-sm text-gray-400 py-6">No hay clientes registrados</p>}
                {list.map((c) => (
                  <label key={c.id} data-testid={`cn-customer-${c.id}`} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} className="w-4 h-4 accent-[#EE3B36]" />
                    <Avatar src={c.avatar} name={c.name} size={32} />
                    <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{c.name}</p><p className="text-[11px] text-gray-500">{c.phone}</p></div>
                    <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{c.orders || 0} pedidos</span>
                  </label>
                ))}
              </div>
            )}

            <Button data-testid="cn-send" onClick={send} disabled={busy} className="w-full bg-[#EE3B36] hover:bg-[#d43530] gap-2 h-11">
              <Send size={16} /> {busy ? 'Enviando...' : `Enviar promoción${targetCount ? ` (${targetCount})` : ''}`}
            </Button>
          </div>
        </Card>

        <Card className="lg:col-span-2" title="Promociones enviadas">
          <ul className="divide-y divide-gray-100 -mx-1" data-testid="cn-history">
            {(!customerCampaigns || customerCampaigns.length === 0) && <li className="text-center text-gray-400 py-12 text-sm">Aún no has enviado promociones</li>}
            {(customerCampaigns || []).map((n) => (
              <li key={n.id} data-testid={`cn-item-${n.id}`} className="flex items-start gap-3 px-1 py-3.5">
                <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"><Bell size={17} /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[#1E2130] truncate">{n.title}</p>
                  <p className="text-sm text-gray-600 line-clamp-2">{n.text}</p>
                  <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.createdAt)} • {n.scope === 'all' ? 'Todos' : 'Seleccionados'} • {n.count} cliente{n.count === 1 ? '' : 's'}</p>
                </div>
                <button data-testid={`cn-delete-${n.id}`} onClick={() => setConfirmDel(n)} className="h-8 w-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center shrink-0"><Trash2 size={15} /></button>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar promoción del historial</AlertDialogTitle>
            <AlertDialogDescription>Esto solo la quita del historial del panel. Los clientes que ya la recibieron la seguirán viendo en su app.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="cn-delete-confirm" className="bg-red-600 hover:bg-red-700" onClick={() => { deleteCustomerCampaign(confirmDel.id).then(() => toast('Promoción eliminada del historial')); setConfirmDel(null); }}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCustomerNotifications;
